/* eslint-disable no-restricted-imports */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { DpProductCard } from '@/components/blocks/DpLibrary/DpProductCard';
import { useProductEvents } from '@/data/Content/_ProductEvents';
import { calculateProductPriceIncrease } from '@/data/Content/BasicCheckOut/utils/calculateProductPriceIncrease';
import { ProductType } from '@/data/types/Product';
import { getImage } from '@/utils/getImage';
import { Grid } from '@mui/material';
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { formatPrice } from 'integration/utils/formatPrice';
import { FC, useMemo } from 'react';

interface InfoElectronico {
	offerDeContado?: string;
	strikedDeContado?: string;
}

export const ProductCard: FC<{
	product: ProductType;
	infoElectronico?: InfoElectronico;
	clickAction?: () => void;
}> = ({ product, clickAction, infoElectronico }) => {
	const { onClick } = useProductEvents({ product });

	const { price, priceWithoutOffer, shouldOffer } = calculateProductPrices({
		list: product.productPrice.list || 0,
		offer: product.productPrice.offer || 0,
		ribbons: product?.ribbons ?? [],
	});

	const category = useMemo(() => {
		const family = findProductAttribute(product.attributes, 'Familia');
		const gender = findProductAttribute(product.attributes, 'Genero');
		const uso = findProductAttribute(product.attributes, 'Uso');
		const brand = findProductAttribute(product.attributes, 'Marca');

		if (uso?.value === 'Electronicos' && brand) {
			return `${brand.value}`;
		}

		if (family && gender) {
			return `${family.value} ${gender.value}`;
		}

		return product.manufacturer;
	}, [product.attributes, product.manufacturer]);

	const prices = useMemo(() => {
		if (infoElectronico) {
			return {
				discount: infoElectronico.strikedDeContado ? infoElectronico.offerDeContado : undefined,
				price: infoElectronico.strikedDeContado || infoElectronico.offerDeContado || '',
			};
		}

		return {
			price: priceWithoutOffer,
			discount: shouldOffer ? price : undefined,
		};
	}, [infoElectronico, price, priceWithoutOffer, shouldOffer]);

	return (
		<Grid onClick={onClick(clickAction)} id={product.partNumber} data-testid={product.partNumber}>
			<DpProductCard
				seo={product?.seo?.href}
				title={product.name}
				category={Array.isArray(category) ? category[0] : category ?? ''}
				image={getImage(product.thumbnail)}
				price={formatPrice(calculateProductPriceIncrease(prices.price), 'mxn')}
				colors={['#C0474A', '#C46D52', '#242424']}
				ribbons={product?.ribbons ?? []}
				showPrice={product.showPrice}
			/>
		</Grid>
	);
};
